/**
 * Clinics Mapper
 * Transforms CMS API responses to domain Clinic types
 * CMS is source of truth - enum values used directly (no mapping)
 */

import type {
  Clinic,
  ClinicsFilters,
  Treatment,
  Service,
  ClinicType,
  Condition,
  PatientGroup,
  ConsultationType,
  Accessibility,
  EmployeeBenefit,
  Financier,
  ClinicLocation,
  ClinicSuccessRates,
  ClinicDayHours,
  ClinicSchedule,
} from "./clinics.types";
import {
  CMS_CONDITIONS,
  CMS_TREATMENT_TYPES,
  CMS_PATIENT_GROUPS,
  CMS_SUPPORTING_SERVICES,
  CMS_CLINIC_TYPES,
  CMS_ACCESSIBILITY,
  CMS_CONSULTATION_TYPES,
  CMS_EMPLOYEE_BENEFITS,
  CMS_FINANCING_OPTIONS,
} from "./clinics.types";
import type { Address, Document, Language } from "@src/packages/types/common";
import { CMS_LANGUAGES } from "@src/packages/types/common";
import type { PaginationParams } from "@src/packages/pagination/pagination.types";
import type { PayloadClinic, PayloadClinicsResponse } from "./payload-types";
import type { Insurance } from "./clinics.types";
import { buildFiltersQueryParams } from "./utils/queryParams";

export type CmsClinicResponse = PayloadClinic;
export type CmsClinicsResponse = PayloadClinicsResponse;

function mapTreatmentType(apiTreatment: string): Treatment | null {
  // Filter out conditions that appear in treatment_types (data quality issue)
  // If value exists in CMS_CONDITIONS, it's actually a condition, not a treatment
  if (CMS_CONDITIONS.includes(apiTreatment as Condition)) {
    return null;
  }

  if (CMS_TREATMENT_TYPES.includes(apiTreatment as Treatment)) {
    return apiTreatment as Treatment;
  }

  console.warn("[clinics.mapper] Unmapped treatment type:", apiTreatment);
  return null;
}

function mapPatientGroup(apiGroup: string): PatientGroup | null {
  if (CMS_PATIENT_GROUPS.includes(apiGroup as PatientGroup)) {
    return apiGroup as PatientGroup;
  }
  console.warn("[clinics.mapper] Unmapped patient group:", apiGroup);
  return null;
}

// Handles both specialties_conditions field AND conditions that appear in treatment_types
function mapCondition(apiCondition: string): Condition | null {
  // Filter out treatments that appear in specialties_conditions (data quality issue)
  // If value exists in CMS_TREATMENT_TYPES, it's actually a treatment, not a condition
  if (CMS_TREATMENT_TYPES.includes(apiCondition as Treatment)) {
    return null;
  }

  if (CMS_CONDITIONS.includes(apiCondition as Condition)) {
    return apiCondition as Condition;
  }

  console.warn("[clinics.mapper] Unmapped condition:", apiCondition);
  return null;
}

function mapService(apiService: string): Service | null {
  if (CMS_SUPPORTING_SERVICES.includes(apiService as Service)) {
    return apiService as Service;
  }
  console.warn("[clinics.mapper] Unmapped service:", apiService);
  return null;
}

function mapClinicType(apiType: string | null): ClinicType {
  if (!apiType) return "Boutique";
  if (CMS_CLINIC_TYPES.includes(apiType as ClinicType)) {
    return apiType as ClinicType;
  }
  return "Boutique";
}

function mapConsultationType(apiType: string): ConsultationType | null {
  if (CMS_CONSULTATION_TYPES.includes(apiType as ConsultationType)) {
    return apiType as ConsultationType;
  }
  return null;
}

function mapAccessibility(apiAccessibility: string): Accessibility | null {
  if (CMS_ACCESSIBILITY.includes(apiAccessibility as Accessibility)) {
    return apiAccessibility as Accessibility;
  }
  console.warn("[clinics.mapper] Unmapped accessibility:", apiAccessibility);
  return null;
}

function mapLanguage(apiLanguage: string): Language {
  const normalized = apiLanguage.toLowerCase();
  if (CMS_LANGUAGES.includes(normalized as Language)) {
    return normalized as Language;
  }
  console.warn("[clinics.mapper] Unmapped language from CMS:", apiLanguage);
  return "en";
}

// Insurance mapping (client-side only) - CMS insurance_name is free text, not an enum
const INSURANCE_LABELS: Record<Insurance, string> = {
  aetna: "Aetna",
  kaiser: "Kaiser",
  bluecross: "BlueCross",
};

const INSURANCE_ALIAS_MAP: Record<string, Insurance> = {
  aetna: "aetna",
  "aetna inc": "aetna",
  "aetna insurance": "aetna",
  kaiser: "kaiser",
  "kaiser permanente": "kaiser",
  "kaiser-permanente": "kaiser",
  bluecross: "bluecross",
  "blue cross": "bluecross",
  "bluecross blueshield": "bluecross",
  "blue cross blue shield": "bluecross",
  "bluecross blue shield": "bluecross",
};

function normalizeInsuranceName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ").replace(/\./g, "");
}

function splitInsuranceNames(raw: string): string[] {
  return raw
    .split(/[;,|]/)
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function mapInsuranceNameToDomain(name: string): Insurance | null {
  const normalized = normalizeInsuranceName(name);
  const mapped =
    INSURANCE_ALIAS_MAP[normalized] ||
    INSURANCE_ALIAS_MAP[normalized.replace(/ /g, "")];
  if (!mapped) {
    console.warn("[clinics.mapper] Unmapped insurance name:", name);
    return null;
  }
  return mapped;
}

function mapClinicInsurances(raw?: string | null): Insurance[] | undefined {
  if (!raw) return undefined;
  const values = splitInsuranceNames(raw);
  const mapped = values
    .map(mapInsuranceNameToDomain)
    .filter((ins): ins is Insurance => ins !== null);
  return mapped.length ? uniqueValues(mapped) : undefined;
}

// Kept for potential future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function mapInsuranceToCms(insurance: Insurance): string {
  return INSURANCE_LABELS[insurance] || insurance;
}

function mapEmployeeBenefitFromCms(value: string): EmployeeBenefit | null {
  if (CMS_EMPLOYEE_BENEFITS.includes(value as EmployeeBenefit)) {
    return value as EmployeeBenefit;
  }
  console.warn("[clinics.mapper] Unmapped employee benefit:", value);
  return null;
}

function mapFinancierFromCms(value: string): Financier | null {
  if (CMS_FINANCING_OPTIONS.includes(value as Financier)) {
    return value as Financier;
  }
  console.warn("[clinics.mapper] Unmapped financing option:", value);
  return null;
}

function uniqueValues<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function mapEmployeeBenefits(
  values?: string[] | null,
): EmployeeBenefit[] | undefined {
  if (!values || values.length === 0) return undefined;
  const mapped = values
    .map(mapEmployeeBenefitFromCms)
    .filter((benefit): benefit is EmployeeBenefit => benefit !== null);
  return mapped.length ? uniqueValues(mapped) : undefined;
}

function mapFinanciers(values?: string[] | null): Financier[] | undefined {
  if (!values || values.length === 0) return undefined;
  const mapped = values
    .map(mapFinancierFromCms)
    .filter((financier): financier is Financier => financier !== null);
  return mapped.length ? uniqueValues(mapped) : undefined;
}

function mapClinicLocation(
  point?: CmsClinicResponse["location"] | null,
): ClinicLocation | null {
  if (!point) return null;

  // Handle GeoJSON Point format: { type: "Point", coordinates: [lng, lat] }
  if (typeof point === "object" && "type" in point && point.type === "Point") {
    const coords = point.coordinates;
    if (!Array.isArray(coords) || coords.length !== 2) return null;
    const [lng, lat] = coords;
    if (typeof lat !== "number" || typeof lng !== "number") return null;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { latitude: lat, longitude: lng };
  }

  // Handle plain array format: [lng, lat]
  if (Array.isArray(point) && point.length === 2) {
    const [lng, lat] = point;
    if (typeof lat !== "number" || typeof lng !== "number") return null;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { latitude: lat, longitude: lng };
  }

  return null;
}

function normalizeNumber(value?: number | null): number | undefined {
  if (typeof value !== "number") return undefined;
  return Number.isNaN(value) ? undefined : value;
}

function mapSuccessRates(
  cmsClinic: CmsClinicResponse,
): ClinicSuccessRates | undefined {
  const rates: ClinicSuccessRates = {
    under35: normalizeNumber(cmsClinic.success_rate_under35),
    between35And37: normalizeNumber(cmsClinic.success_rate_35_37),
    between38And40: normalizeNumber(cmsClinic.success_rate_38_40),
    between41And42: normalizeNumber(cmsClinic.success_rate_41_42),
    over42: normalizeNumber(cmsClinic.success_rate_over42),
  };
  return Object.values(rates).some((value) => value !== undefined)
    ? rates
    : undefined;
}

function parseDayHours(raw: string | null | undefined): ClinicDayHours | null {
  if (!raw || raw.toLowerCase() === "closed") return null;
  const dash = raw.lastIndexOf("-");
  if (dash <= 0) return null;
  const open = raw.slice(0, dash).trim();
  const close = raw.slice(dash + 1).trim();
  if (!open || !close) return null;
  return { open, close };
}

function mapSchedule(
  raw: CmsClinicResponse["opening_hours"],
): ClinicSchedule | undefined {
  if (!raw?.local) return undefined;
  const { local, utc_standard_offset } = raw;
  const schedule: ClinicSchedule = {
    mon: parseDayHours(local.mon),
    tue: parseDayHours(local.tue),
    wed: parseDayHours(local.wed),
    thu: parseDayHours(local.thu),
    fri: parseDayHours(local.fri),
    sat: parseDayHours(local.sat),
    sun: parseDayHours(local.sun),
    utcOffset: utc_standard_offset ?? null,
  };
  // Return undefined if no day has hours (all null)
  const hasSomeHours = (
    ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const
  ).some((d) => schedule[d] !== null && schedule[d] !== undefined);
  return hasSomeHours ? schedule : undefined;
}

export function transformCmsClinicToClinic(
  cmsClinic: CmsClinicResponse,
): Clinic {
  const treatments: Treatment[] = (cmsClinic.treatment_types || [])
    .map(mapTreatmentType)
    .filter((t): t is Treatment => t !== null);

  const patientGroups: PatientGroup[] = (cmsClinic.patient_groups || [])
    .map(mapPatientGroup)
    .filter((pg): pg is PatientGroup => pg !== null);

  const conditionsFromSpecialties: Condition[] = (
    cmsClinic.specialties_conditions || []
  )
    .map(mapCondition)
    .filter((c): c is Condition => c !== null);

  const conditionsFromTreatments: Condition[] = (
    cmsClinic.treatment_types || []
  )
    .map(mapCondition)
    .filter((c): c is Condition => c !== null);

  const conditions: Condition[] = Array.from(
    new Set([...conditionsFromSpecialties, ...conditionsFromTreatments]),
  );

  const services: Service[] = (cmsClinic.supporting_services || [])
    .map(mapService)
    .filter((s): s is Service => s !== null);

  const consultationTypes: ConsultationType[] = (
    cmsClinic.consultation_types || []
  )
    .map(mapConsultationType)
    .filter((ct): ct is ConsultationType => ct !== null);

  const accessibilities: Accessibility[] = (cmsClinic.accessibility || [])
    .map(mapAccessibility)
    .filter((a): a is Accessibility => a !== null);

  const languages: Language[] = (cmsClinic.languages || []).map(mapLanguage);

  const address: Address = {
    id: `addr_${cmsClinic.id}`,
    address1: cmsClinic.address_line_1 || "",
    address2: cmsClinic.address_line_2 || null,
    city: cmsClinic.city || "",
    state: cmsClinic.state || null,
    postalCode: cmsClinic.zip || "",
    country: cmsClinic.country || "",
  };

  const images: Document[] = (cmsClinic.images || [])
    .filter(
      (img): img is Exclude<typeof img, string> => typeof img === "object",
    )
    .map((img) => ({
      id: String(img.id),
      url: img.url || "",
      name: img.filename || `image_${img.id}`,
      type: img.mimeType || "image/webp",
    }));

  const location = mapClinicLocation(cmsClinic.location);
  const employeeBenefits = mapEmployeeBenefits(
    cmsClinic.in_network_employee_benefits,
  );
  const financiers = mapFinanciers(cmsClinic.financing_options);
  const insuranceAccepted =
    typeof cmsClinic.insurance_accepted === "boolean"
      ? cmsClinic.insurance_accepted
      : undefined;
  const insuranceProviders =
    cmsClinic.insurance_providers?.map((p) => p.name) ?? [];
  const insuranceName =
    insuranceProviders.length > 0 ? insuranceProviders.join("; ") : null;
  const insurances = mapClinicInsurances(insuranceName);
  const successRates = mapSuccessRates(cmsClinic);
  const nbCyclePerYear = normalizeNumber(cmsClinic.nb_cycle_per_year);
  const lastReportYear = normalizeNumber(cmsClinic.last_report_year);

  return {
    id: String(cmsClinic.id),
    name: cmsClinic.name || "Unnamed Clinic",
    address,
    headline: cmsClinic.name || "Unnamed Clinic",
    images,
    tags: null,
    services,
    languages: languages.length > 0 ? languages : ["en"],
    clinicType: mapClinicType(cmsClinic.type ?? null),
    conditions,
    treatments,
    consultationTypes:
      consultationTypes.length > 0 ? consultationTypes : ["In person"],
    patientGroups,
    accessibilities,
    website: cmsClinic.website || null,
    email: cmsClinic.email || null,
    phone: cmsClinic.phone || null,
    bookingUrl: cmsClinic.booking_url || null,
    location,
    insuranceAccepted,
    insuranceName,
    insuranceProviders,
    insurances,
    financiers,
    employeeBenefits,
    schedule: mapSchedule(cmsClinic.opening_hours),
    isFirstConsultationFree:
      typeof cmsClinic.free_first_consult === "boolean"
        ? cmsClinic.free_first_consult
        : undefined,
    hasReportedAnnualIvfData: Array.isArray(cmsClinic.reported_to)
      ? cmsClinic.reported_to.length > 0
      : Boolean(cmsClinic.reported_to) || Boolean(cmsClinic.last_report_year),
    nbCyclePerYear,
    reportedTo: Array.isArray(cmsClinic.reported_to)
      ? cmsClinic.reported_to.join(", ") || null
      : cmsClinic.reported_to || null,
    lastReportYear,
    publicNotes: cmsClinic.public_notes_en || null,
    lastUpdatedIso: cmsClinic.last_updated_iso || cmsClinic.updatedAt || null,
    successRates,
    // New patient and donor rates come from CDC build script, not CMS
    newPatientSuccessRates: undefined,
    donorSuccessRates: undefined,
    hasOnSiteLab: cmsClinic.lab_on_site ?? false,
    establishedYear: cmsClinic.established ?? undefined,
    team: (cmsClinic.doctors ?? []).map((doc) => ({
      name: [doc.first_name, doc.last_name].filter(Boolean).join(" "),
      title: doc.specialty ?? "",
      role: doc.specialty ?? "",
      imageUrl:
        typeof doc.picture === "object" && doc.picture?.url
          ? doc.picture.url
          : undefined,
    })),
  };
}

export function buildQueryParams(
  filters?: ClinicsFilters,
  pagination?: PaginationParams,
): string {
  const params = new URLSearchParams();

  if (pagination) {
    params.append("page", String(pagination.page));
    params.append("limit", String(pagination.limit));
  }

  if (filters) {
    const filterParams = buildFiltersQueryParams(filters);
    // Merge filter params into main params
    const filterUrlParams = new URLSearchParams(filterParams);
    filterUrlParams.forEach((value, key) => {
      params.append(key, value);
    });
  }

  return params.toString();
}
